import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { toPlainText } from "@/lib/markdown";
import { ErrorBoundary } from "@/components/error-boundary";
import MarkdownRenderer from "./MarkdownRenderer";

import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import MessageTimestamp from "./MessageTimestamp";
import { MessageSendingStatus } from "@/indexedDB/types";
import { extractEmojiFromMessage } from "@/utils/messageUtils";
import {
  useExpandableMessage,
  GRADIENT_HEIGHT,
} from "@/hooks/useExpandableMessage";

interface MessageTextProps {
  readonly text: string;
  readonly isOutgoingMessage: boolean;
  readonly isPinned?: boolean;
  readonly sendingStatus: MessageSendingStatus;
  readonly deliveryUnconfirmed?: boolean;
  readonly timestamp: string;
  readonly hasMediaAttachment: boolean;
  readonly tags?: string[];
  /**
   * Render the timestamp inline with the last line of text (Telegram-style).
   * File messages opt out (withTimestamp={false}) so the timestamp sits on its
   * own line below the caption instead of being shoved next to short text in a
   * file card's wide bubble.
   */
  readonly withTimestamp?: boolean;
}

export default function MessageText({
  text,
  isOutgoingMessage,
  isPinned = false,
  sendingStatus,
  deliveryUnconfirmed,
  timestamp,
  hasMediaAttachment,
  tags,
  withTimestamp = true,
}: MessageTextProps) {
  const { t } = useTranslation();
  const { isEmojiOnlyMessage, emojiClassName } = extractEmojiFromMessage(text);

  const plainTextLength = useMemo(() => toPlainText(text).length, [text]);

  const {
    contentRef,
    maxHeight,
    isTruncated,
    isFullyExpanded,
    expand,
    collapse,
  } = useExpandableMessage(plainTextLength);

  const showExpandButton = isTruncated || isFullyExpanded;

  return (
    <div
      className={cn(
        "min-w-0 select-none rounded-md p-1 text-left text-base font-normal leading-tight lg:select-text",
        hasMediaAttachment && {
          "mt-1": true,
          "!bg-primary": isOutgoingMessage,
          "!bg-accent": !isOutgoingMessage,
        },
        isEmojiOnlyMessage && {
          ...(emojiClassName ? { [emojiClassName]: true } : {}),
          "bg-transparent": true,
          "dark:bg-transparent": true,
          "text-right": isOutgoingMessage,
        },
      )}
    >
      <div className="relative">
        <div
          ref={contentRef}
          style={
            maxHeight === undefined
              ? undefined
              : { maxHeight: `${maxHeight}px`, overflow: "hidden" }
          }
        >
          <div className="flex grow flex-wrap items-start justify-end gap-1">
            <div className="min-w-0 grow overflow-hidden">
              {isEmojiOnlyMessage ? (
                <span className="px-1">{toPlainText(text)}</span>
              ) : (
                // Defense-in-depth for #914: if a browser extension mutates the
                // rendered markdown DOM and desyncs React (NotFoundError on
                // insertBefore), contain the crash to this one message and
                // degrade it to non-translatable plain text instead of taking
                // down the whole message list / route. `resetKeys={[text]}`
                // lets an edited message re-attempt the markdown render.
                <ErrorBoundary
                  resetKeys={[text]}
                  fallback={
                    <p
                      className="notranslate whitespace-pre-wrap break-words px-1 text-base leading-snug"
                      translate="no"
                    >
                      {toPlainText(text)}
                    </p>
                  }
                >
                  <MarkdownRenderer
                    className={cn(
                      "px-1",
                      "prose-sm max-w-none break-words text-base leading-snug",
                    )}
                    content={text}
                    isOutgoingMessage={isOutgoingMessage}
                    tags={tags}
                  />
                </ErrorBoundary>
              )}
            </div>
            {withTimestamp && (
              <div className="ml-auto shrink-0 self-end">
                <MessageTimestamp
                  className="select-none"
                  timestamp={timestamp}
                  isOutgoing={isOutgoingMessage}
                  isPinned={isPinned}
                  sendingStatus={sendingStatus}
                  deliveryUnconfirmed={deliveryUnconfirmed}
                />
              </div>
            )}
          </div>
        </div>

        {isTruncated && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: `${GRADIENT_HEIGHT}px` }}
            aria-hidden="true"
          >
            <div
              className={cn(
                "h-full w-full bg-gradient-to-b",
                isOutgoingMessage
                  ? "from-primary/0 to-primary"
                  : "from-accent/0 to-accent",
              )}
            />
          </div>
        )}
      </div>

      {showExpandButton && (
        <button
          type="button"
          onClick={isTruncated ? expand : collapse}
          aria-expanded={!isTruncated}
          className={cn(
            "mx-auto mt-1.5 flex items-center gap-1 rounded-full px-3 py-0.5 text-xs font-medium transition-colors",
            isOutgoingMessage
              ? "bg-primary-foreground/10 text-primary-foreground/90 hover:bg-primary-foreground/20"
              : "bg-accent-foreground/10 text-accent-foreground/90 hover:bg-accent-foreground/20",
          )}
        >
          {isTruncated ? (
            <>
              {t("message.showMore")}
              <ChevronDownIcon className="size-3.5" />
            </>
          ) : (
            <>
              {t("message.showLess")}
              <ChevronUpIcon className="size-3.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
}
